<template>
  <div class="stat-tile" :class="{ 'is-loading': loading }">
    <div v-if="icon" class="stat-icon" :class="`tone-${tone}`">
      <font-awesome-icon :icon="icon" />
    </div>
    <div class="stat-body">
      <span class="stat-label">{{ label }}</span>
      <span class="stat-value">
        <template v-if="loading">
          <span class="stat-skeleton"></span>
        </template>
        <template v-else>{{ displayValue }}</template>
      </span>
      <span v-if="hint" class="stat-hint">{{ hint }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StatTile',
  props: {
    label: { type: String, required: true },
    value: { type: [Number, String], default: 0 },
    hint: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    icon: { type: String, default: '' },
    tone: {
      type: String,
      default: 'accent',
      validator: (v) => ['accent', 'good', 'warning', 'critical', 'neutral'].includes(v),
    },
  },
  computed: {
    displayValue() {
      if (typeof this.value === 'number') {
        return this.value.toLocaleString();
      }
      return this.value;
    },
  },
};
</script>

<style scoped>
.stat-tile {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  min-width: 150px;
  flex: 1 1 180px;
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.stat-tile:hover {
  box-shadow: 0 6px 16px rgba(11, 11, 11, 0.08);
  transform: translateY(-1px);
}

.stat-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: rgba(42, 120, 214, 0.12);
  color: var(--series-1);
}

.stat-icon.tone-good {
  background: rgba(12, 163, 12, 0.12);
  color: var(--status-good);
}

.stat-icon.tone-warning {
  background: rgba(250, 178, 25, 0.18);
  color: #ad7300;
}

.stat-icon.tone-critical {
  background: rgba(208, 59, 59, 0.12);
  color: var(--status-critical);
}

.stat-icon.tone-neutral {
  background: rgba(137, 135, 129, 0.14);
  color: var(--text-secondary);
}

.stat-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.stat-label {
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--text-secondary);
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.25;
}

.stat-skeleton {
  display: inline-block;
  width: 48px;
  height: 18px;
  border-radius: 4px;
  background: var(--gridline);
}

.stat-hint {
  font-size: 12px;
  color: var(--text-muted);
}
</style>
